import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

/**
 * IntelligenceBanner
 * Shows legacy undo info when present and weekly review prompt for consumption-v2 flow.
 */
const IntelligenceBanner = () => {
  const [inference, setInference] = useState(null);
  const [reviewPrompt, setReviewPrompt] = useState(null);
  const [isVetoing, setIsVetoing] = useState(false);

  const fetchActiveInference = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setInference(null);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/intelligence/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && Array.isArray(res.data.inferences) && res.data.inferences.length > 0) {
        setInference(res.data.inferences[0]);
      } else {
        setInference(null);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('authToken');
      }
      setInference(null);
    }
  };

  const fetchReviewPrompt = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setReviewPrompt(null);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/consumptions/review-trigger-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success && res.data?.trigger?.shouldPrompt) {
        setReviewPrompt(res.data.trigger);
      } else {
        setReviewPrompt(null);
      }
    } catch (_err) {
      setReviewPrompt(null);
    }
  };

  const handleVeto = async () => {
    if (!inference || isVetoing) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      setInference(null);
      return;
    }

    setIsVetoing(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/intelligence/veto`,
        { inferenceId: inference.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInference(null);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('authToken');
      }
    } finally {
      setIsVetoing(false);
    }
  };

  useEffect(() => {
    fetchActiveInference();
    fetchReviewPrompt();

    const interval = setInterval(() => {
      fetchActiveInference();
      fetchReviewPrompt();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (!inference && !reviewPrompt) return null;

  if (!inference && reviewPrompt) {
    return (
      <div className="mx-6 mb-6 p-4 bg-slate-900 text-white rounded-[2rem] shadow-2xl animate-fade-in-up border border-primary/30 flex flex-col md:flex-row items-center justify-between gap-4 overflow-hidden relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-50 pointer-events-none" />

        <div className="flex items-center gap-4 relative z-10">
          <div className="size-12 bg-primary/20 rounded-2xl flex items-center justify-center border border-primary/40 shadow-inner group-hover:scale-110 transition-transform">
            <span className="material-symbols-outlined text-primary scale-125 fill-1">checklist</span>
          </div>
          <div>
            <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary mb-1">Mutfak Isletim Sistemi</h4>
            <p className="text-sm font-bold text-white leading-tight">Haftalik tuketim review listesi hazir.</p>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              Pending kayitlar: {Number(reviewPrompt.pendingCount || 0)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 relative z-10">
          <button
            onClick={() => {
              window.location.href = '/my-kitchen';
            }}
            className="px-8 py-3 bg-primary hover:bg-primary/90 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all shadow-lg shadow-primary/20 active:scale-95 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
            REVIEW'E GIT
          </button>

          <button
            onClick={() => setReviewPrompt(null)}
            className="p-3 text-slate-500 hover:text-white transition-colors"
            title="Yoksay"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-6 mb-6 p-4 bg-slate-900 text-white rounded-[2rem] shadow-2xl animate-fade-in-up border border-primary/30 flex flex-col md:flex-row items-center justify-between gap-4 overflow-hidden relative group">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-50 pointer-events-none" />

      <div className="flex items-center gap-4 relative z-10">
        <div className="size-12 bg-primary/20 rounded-2xl flex items-center justify-center border border-primary/40 shadow-inner group-hover:scale-110 transition-transform">
          <span className="material-symbols-outlined text-primary scale-125 fill-1">psychology_alt</span>
        </div>
        <div>
          <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary mb-1">Mutfak Isletim Sistemi</h4>
          <p className="text-sm font-bold text-white leading-tight">Legacy inference kaydi bulundu.</p>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Yanlissa geri alabilirsin.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 relative z-10">
        <button
          onClick={handleVeto}
          disabled={isVetoing}
          className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all shadow-lg shadow-red-600/20 active:scale-95 disabled:opacity-50 flex items-center gap-2"
        >
          {isVetoing ? (
            <span className="material-symbols-outlined animate-spin text-[18px]">sync</span>
          ) : (
            <span className="material-symbols-outlined text-[18px]">undo</span>
          )}
          {isVetoing ? 'GERI ALINIYOR...' : 'SIMDI GERI AL'}
        </button>

        <button
          onClick={() => setInference(null)}
          className="p-3 text-slate-500 hover:text-white transition-colors"
          title="Yoksay"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>
      </div>
    </div>
  );
};

export default IntelligenceBanner;
